"use client";

import { makeFunctionReference } from "convex/server";
import { useMutation } from "convex/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { normalizeTenantSlug } from "@/core/auth/tenant-onboarding";

const createTenant = makeFunctionReference<"mutation", { name: string; slug: string }, { tenantId: string; slug: string }>(
  "tenants:createTenant",
);

/**
 * Shown when an authenticated user has no active tenant membership.
 * Replaces the old dead-end "No active tenant" message with a guided
 * three-way first run.
 */
export function FirstRunScreen() {
  const create = useMutation(createTenant);
  const [companyName, setCompanyName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function createWorkspace() {
    setBusy(true);
    setError(null);
    try {
      const result = await create({ name: companyName.trim(), slug: normalizeTenantSlug(companyName) });
      window.localStorage.setItem("haulbase.selectedTenantId", result.tenantId);
      window.location.assign("/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create workspace");
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 py-12">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Welcome to Haulbase</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your workspace is empty. Pick a starting point.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <FirstRunCard
          title="Create your workspace"
          body="Set up your company workspace, then add your first load."
          action={
            <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); void createWorkspace(); }}>
              <label className="block text-sm">
                Company name
                <input className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3" value={companyName} onChange={(event) => setCompanyName(event.target.value)} required maxLength={48} />
              </label>
              <Button className="w-full" disabled={busy || !companyName.trim()}>
                {busy ? "Creating…" : "Create workspace"}
              </Button>
            </form>
          }
        />
        <FirstRunCard
          title="Import from your TMS"
          body="Migrate loads, carriers, customers, and SOPs from McLeod, MercuryGate, or Aljex."
          action={
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => window.location.assign("mailto:support@haulbase.ai?subject=TMS%20migration")}
            >
              Talk to us
            </Button>
          }
        />
        <FirstRunCard
          title="Explore the agent demo"
          body="Preview agent workflows before adding your own freight data."
          action={
            <Button variant="secondary" className="w-full" onClick={() => window.location.assign("/agent-demo")}>
              Explore demo
            </Button>
          }
        />
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </main>
  );
}

function FirstRunCard({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
      <div className="flex-1 space-y-1">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
      {action}
    </div>
  );
}
